import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { resolveMutationErrorMessage } from '~/data/utils';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type UserSettingsAdmin } from '../types';
import { zodUserSettingsAdminSchema } from '../validations';

import { type UpdateUserSettingsAdminPayload } from './types';
import {
  zodUpdateUserSettingsAdminEndpointSchema,
  zodUpdateUserSettingsAdminPayloadSchema,
} from './validations';

// 400: the caller is not a VESTIGAS admin with write access, or the user does not exist;
// 422: a value is not a boolean or null.
const ERROR_MESSAGE_BY_STATUS: Record<number, () => string> = {
  400: () =>
    i18n.t(($) => $.user.updateUserSettingsAdmin.error400, { ns: 'data' }),
  422: () =>
    i18n.t(($) => $.user.updateUserSettingsAdmin.error422, { ns: 'data' }),
};

/**
 * Updates the settings stored on a user. Requires VESTIGAS admin write access.
 *
 * @param userId - The ID of the user whose settings to update.
 * @param payload - The settings to merge into the stored ones.
 * @returns The user's settings after the update.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/update_user_settings_vadmin_user__user_id__settings_post
 */
export const updateUserSettingsAdmin = async (
  userId: UUID,
  payload: UpdateUserSettingsAdminPayload,
) => {
  try {
    return await vestigasApi
      .post(ENDPOINT.USER.SETTINGS_ADMIN.UPDATE(userId), {
        json: payload,
        validate: {
          body: zodUpdateUserSettingsAdminPayloadSchema,
          pathSegments: zodUpdateUserSettingsAdminEndpointSchema,
          response: zodUserSettingsAdminSchema,
        },
      })
      .json<UserSettingsAdmin>();
  } catch (error) {
    log.error({
      message: 'Error updating user settings',
      error,
    });

    const errorMessage = resolveMutationErrorMessage(
      error,
      ERROR_MESSAGE_BY_STATUS,
      () =>
        i18n.t(($) => $.user.updateUserSettingsAdmin.error.generic, {
          ns: 'data',
        }),
    );
    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    throw error;
  }
};
